import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Star, Zap, Loader2, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const PLANS_DATA = [
  {
    name: "Free",
    planKey: "Free",
    usdPrice: "$0",
    period: "forever",
    accent: "aqua",
    tagline: "Essential starter tools",
    creditsLabel: "Free Trial Limits",
    features: [
      "1 Customized CV per month",
      "1 Tailored Cover Letter",
      "3 ATS Score scans",
      "5 Job Searches per day",
      "1 Voice Interview trial session",
      "Application Tracker Kanban",
    ],
    cta: "Start Free",
  },
  {
    name: "Career Pro",
    planKey: "Career Pro",
    usdPrice: "$24.99",
    period: "/month",
    accent: "electric",
    featured: true,
    tagline: "For active job seekers",
    creditsLabel: "⚡ 100 Credits / month",
    features: [
      "100 AI Credits monthly",
      "Unlimited Job Searches & Match filters",
      "Deep ATS intelligence & gap discovery",
      "AI CV customization & rewriting",
      "4-language CV translation",
      "Full Application Tracker sync",
      "STAR behavioral interview feedback",
    ],
    cta: "Subscribe to Career Pro",
  },
  {
    name: "Interview Pro",
    planKey: "Interview Pro",
    usdPrice: "$29.99",
    period: "/month",
    accent: "violet",
    tagline: "Full executive suite & voice rooms",
    creditsLabel: "⚡ 150 Credits / month",
    features: [
      "150 AI Credits monthly",
      "Everything in Career Pro",
      "Live Face-to-Face voice mock rooms",
      "Up to 60 mins live voice simulation",
      "Priority AI model response latency",
      "Exclusive Q&A question banks",
    ],
    cta: "Subscribe to Interview Pro",
  },
];

const PACKS_DATA = [
  {
    name: "Quick Pack",
    packKey: "Quick Pack",
    credits: 30,
    usdPrice: "$9.99",
    tagline: "Ideal for 1-2 upcoming interviews",
    features: [
      "30 Top-up Credits",
      "90-day validity rollover",
      "Valid for ATS & CV customization",
      "Valid for STAR answer coaching",
    ],
    cta: "Get Quick Pack",
  },
  {
    name: "Sprint Pack",
    packKey: "Sprint Pack",
    credits: 60,
    usdPrice: "$17.99",
    featured: true,
    tagline: "Most popular for active interview rounds",
    features: [
      "60 Top-up Credits",
      "90-day validity rollover",
      "Supports 20 CV custom revisions",
      "Full access to STAR feedback & Q&A",
    ],
    cta: "Get Sprint Pack",
  },
  {
    name: "Intensive Pack",
    packKey: "Intensive Pack",
    credits: 100,
    usdPrice: "$27.99",
    tagline: "Comprehensive career sprint",
    features: [
      "100 Top-up Credits",
      "90-day validity rollover",
      "Highest volume savings per credit",
      "Supports extensive mock practice",
    ],
    cta: "Get Intensive Pack",
  },
];

const ACCENT = {
  aqua: { ring: "ring-aqua/30", dot: "text-aqua", badge: "bg-aqua/10 text-aqua" },
  electric: { ring: "ring-electric", dot: "text-electric", badge: "bg-electric text-white" },
  violet: { ring: "ring-violet/30", dot: "text-violet", badge: "bg-violet/10 text-violet" },
};

export default function PricingCards() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useLanguage();

  const searchParams = new URLSearchParams(location.search);
  const planParam = searchParams.get("plan");
  const packParam = searchParams.get("pack");

  const [tab, setTab] = useState(packParam ? "packs" : "plans"); // "plans" | "packs"
  const [loadingKey, setLoadingKey] = useState(null);

  const handleCheckout = async (item) => {
    if (!user) {
      if (item.planKey && item.planKey !== "Free") {
        navigate(`/signup?plan=${encodeURIComponent(item.planKey)}`);
      } else if (item.packKey) {
        navigate(`/signup?pack=${encodeURIComponent(item.packKey)}`);
      } else {
        navigate("/signup");
      }
      return;
    }

    if (item.planKey === "Free") {
      navigate("/app");
      return;
    }

    const itemKey = item.planKey || item.packKey;
    setLoadingKey(itemKey);
    try {
      const payload = {
        currency: "USD",
        plan: item.planKey || undefined,
        pack: item.packKey || undefined,
        success_url: `${window.location.origin}/app?payment=success`,
        cancel_url: `${window.location.origin}/app/pricing?payment=cancelled`,
      };
      const { data } = await api.post("/payments/create-checkout", payload);
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Could not initiate checkout session.");
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed to start checkout. Please try again.");
    } finally {
      setLoadingKey(null);
    }
  };

  return (
    <div className="flex flex-col items-center">
      {/* Plans vs Packs Toggle */}
      <div className="mb-10 flex items-center justify-center">
        <div className="flex rounded-full border border-border bg-card p-1 shadow-xs">
          <button
            onClick={() => setTab("plans")}
            data-testid="toggle-plans"
            className={`rounded-full px-6 py-2 text-sm font-semibold transition-all ${
              tab === "plans" ? "bg-primary text-primary-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Monthly Plans
          </button>
          <button
            onClick={() => setTab("packs")}
            data-testid="toggle-packs"
            className={`flex items-center gap-1.5 rounded-full px-6 py-2 text-sm font-semibold transition-all ${
              tab === "packs" ? "bg-primary text-primary-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Zap className="h-3.5 w-3.5" /> Credit Top-Up Packs
          </button>
        </div>
      </div>

      {/* Plans Grid */}
      {tab === "plans" ? (
        <div className="grid w-full gap-6 md:grid-cols-3">
          {PLANS_DATA.map((p, i) => {
            const a = ACCENT[p.accent] || ACCENT.electric;
            const isLoading = loadingKey === p.planKey;
            const isSelectedFromLanding = planParam === p.planKey;

            return (
              <motion.div
                key={p.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className={`relative flex flex-col rounded-3xl border bg-card p-7 ${
                  isSelectedFromLanding
                    ? "ring-2 ring-electric shadow-float md:-translate-y-3 border-electric"
                    : p.featured ? `ring-2 ${a.ring} shadow-float md:-translate-y-3` : "border-border"
                }`}
                data-testid={`pricing-${p.name.toLowerCase().replace(/\s/g, "-")}`}
              >
                {isSelectedFromLanding ? (
                  <span className="absolute -top-3 left-7 inline-flex items-center gap-1 rounded-full bg-electric px-3 py-1 text-xs font-bold text-white shadow-xs">
                    <Sparkles className="h-3 w-3 fill-white" /> Selected Plan
                  </span>
                ) : p.featured ? (
                  <span className="absolute -top-3 left-7 inline-flex items-center gap-1 rounded-full bg-electric px-3 py-1 text-xs font-bold text-white shadow-xs">
                    <Star className="h-3 w-3 fill-white" /> Most Popular
                  </span>
                ) : null}
                <div className="flex items-center justify-between">
                  <p className={`label-caps font-bold ${a.dot}`}>{p.name}</p>
                  {p.creditsLabel && (
                    <span className="rounded-full bg-secondary px-2.5 py-0.5 text-[11px] font-bold text-foreground">
                      {p.creditsLabel}
                    </span>
                  )}
                </div>
                <div className="mt-3 flex items-end gap-1">
                  <span className="font-heading text-4xl font-black">{p.usdPrice}</span>
                  <span className="mb-1 text-sm text-muted-foreground">{p.period}</span>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{p.tagline}</p>

                <ul className="mt-6 flex-1 space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check className={`mt-0.5 h-4 w-4 shrink-0 ${a.dot}`} /> {f}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleCheckout(p)}
                  disabled={isLoading}
                  className={`mt-7 flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-colors disabled:opacity-50 ${
                    p.featured
                      ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow"
                      : "border border-border hover:bg-secondary text-foreground"
                  }`}
                >
                  {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                  {p.cta}
                </button>
              </motion.div>
            );
          })}
        </div>
      ) : (
        /* Packs Grid */
        <div className="grid w-full gap-6 md:grid-cols-3">
          {PACKS_DATA.map((pack, i) => {
            const isLoading = loadingKey === pack.packKey;
            const isSelectedFromLanding = packParam === pack.packKey;

            return (
              <motion.div
                key={pack.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className={`relative flex flex-col rounded-3xl border bg-card p-7 ${
                  isSelectedFromLanding
                    ? "ring-2 ring-electric shadow-float md:-translate-y-3 border-electric"
                    : pack.featured
                    ? "ring-2 ring-electric shadow-float md:-translate-y-3 border-electric/40"
                    : "border-border"
                }`}
                data-testid={`pack-${pack.name.toLowerCase().replace(/\s/g, "-")}`}
              >
                {isSelectedFromLanding ? (
                  <span className="absolute -top-3 left-7 inline-flex items-center gap-1 rounded-full bg-electric px-3 py-1 text-xs font-bold text-white shadow-xs">
                    <Sparkles className="h-3 w-3 fill-white" /> Selected Pack
                  </span>
                ) : pack.featured ? (
                  <span className="absolute -top-3 left-7 inline-flex items-center gap-1 rounded-full bg-electric px-3 py-1 text-xs font-bold text-white shadow-xs">
                    <Sparkles className="h-3 w-3 fill-white" /> Recommended Pack
                  </span>
                ) : null}
                <div className="flex items-center justify-between">
                  <p className="label-caps font-bold text-electric">{pack.name}</p>
                  <span className="flex items-center gap-1 rounded-full bg-electric/10 px-2.5 py-0.5 text-xs font-bold text-electric">
                    <Zap className="h-3 w-3 fill-electric" /> {pack.credits} Credits
                  </span>
                </div>

                <div className="mt-3 flex items-end gap-1">
                  <span className="font-heading text-4xl font-black">{pack.usdPrice}</span>
                  <span className="mb-1 text-xs text-muted-foreground">one-time</span>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{pack.tagline}</p>

                <ul className="mt-6 flex-1 space-y-3">
                  {pack.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-electric" /> {f}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleCheckout(pack)}
                  disabled={isLoading}
                  className={`mt-7 flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-colors disabled:opacity-50 ${
                    pack.featured
                      ? "bg-electric text-white hover:bg-electric/90 shadow-glow"
                      : "border border-border hover:bg-secondary text-foreground"
                  }`}
                >
                  {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                  {pack.cta}
                </button>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
